/**
 * nld-chat.js — on-site AI material advisor widget.
 *
 * Pairs with sections/nld-chat.liquid. No frameworks, no build, ES2020+.
 * Sends the conversation to the Railway backend's POST /chat and renders the
 * reply + live product cards. No secrets here — the backend holds those.
 *
 * Repo source-of-truth: tasks/2026-06-14-llm-chatbot/theme/assets/nld-chat.js
 */
(function () {
  'use strict';

  const root = document.querySelector('[data-nld-chat]');
  if (!root) return;

  const endpointBase = (readMeta('nld-chat-endpoint') || '').replace(/\/+$/, '');
  const ENDPOINT = endpointBase ? endpointBase + '/chat' : '';

  // Stable per-browser-session id so the backend can group turns into one
  // conversation (for analytics). Survives page navigations in the same tab.
  const SESSION_ID = getSessionId();
  function getSessionId() {
    try {
      let id = sessionStorage.getItem('nld-chat-session');
      if (!id) {
        id = (self.crypto && crypto.randomUUID) ? crypto.randomUUID()
             : 'sess-' + Date.now() + '-' + Math.random().toString(36).slice(2);
        sessionStorage.setItem('nld-chat-session', id);
      }
      return id;
    } catch (e) {
      return 'sess-' + Date.now() + '-' + Math.random().toString(36).slice(2);
    }
  }

  const panel = root.querySelector('[data-nld-chat-panel]');
  const log = root.querySelector('[data-nld-chat-log]');
  const form = root.querySelector('[data-nld-chat-form]');
  const input = root.querySelector('[data-nld-chat-input]');
  const sendBtn = root.querySelector('[data-nld-chat-send]');
  const botName = root.dataset.botName || 'Material Advisor';
  const welcome = root.dataset.welcome || 'Hi! Tell me what you’re looking for and I’ll find materials for you.';

  const STARTERS = [
    'Matte wood laminate for a kitchen',
    'Marble look for my living room',
    'Water-resistant laminate under ₹1500',
  ];

  // Conversation state — mirrored to sessionStorage so the chat survives page
  // navigations within the tab (cleared automatically when the tab closes).
  const LS_LOG = 'nld-chat-log', LS_MSGS = 'nld-chat-msgs', LS_OPEN = 'nld-chat-open';
  const messages = [];
  let booted = false;
  let busy = false;

  function saveState() {
    try {
      sessionStorage.setItem(LS_LOG, log.innerHTML);
      sessionStorage.setItem(LS_MSGS, JSON.stringify(messages));
    } catch (e) {}
  }
  function restoreState() {
    try {
      const savedMsgs = JSON.parse(sessionStorage.getItem(LS_MSGS) || '[]');
      const savedLog = sessionStorage.getItem(LS_LOG);
      if (savedMsgs.length && savedLog) {     // only after a real exchange
        messages.push(...savedMsgs);
        log.innerHTML = savedLog;             // restores bubbles + product/handoff cards (all <a>)
        scroll();
        return true;
      }
    } catch (e) {}
    return false;
  }

  // ---- Open / close ------------------------------------------------------
  root.querySelector('[data-nld-chat-open]').addEventListener('click', open);
  root.querySelector('[data-nld-chat-close]').addEventListener('click', close);

  function open() {
    panel.hidden = false;
    root.dataset.open = 'true';
    try { sessionStorage.setItem(LS_OPEN, 'true'); } catch (e) {}
    if (!booted) { boot(); booted = true; }
    setTimeout(() => input.focus(), 50);
  }
  function close() {
    panel.hidden = true;
    root.dataset.open = 'false';
    try { sessionStorage.setItem(LS_OPEN, 'false'); } catch (e) {}
  }

  // Let any element on the page open the chat — e.g. a landing-page CTA.
  // Use <a href="#material-advisor"> or add [data-open-chat] to any element.
  document.addEventListener('click', (e) => {
    const trigger = e.target.closest && e.target.closest('a[href="#material-advisor"], [data-open-chat]');
    if (trigger) { e.preventDefault(); open(); }
  });

  function boot() {
    if (restoreState()) return;      // continue the prior conversation across pages
    appendBot(welcome);
    if (!ENDPOINT) {
      appendBot('⚠️ This assistant isn’t connected yet (missing backend URL in the section settings).');
      return;
    }
    renderChips(STARTERS);
  }

  // ---- Composer ----------------------------------------------------------
  form.addEventListener('submit', (e) => { e.preventDefault(); submit(); });
  input.addEventListener('keydown', (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); submit(); }
  });
  input.addEventListener('input', autosize);

  function autosize() {
    input.style.height = 'auto';
    input.style.height = Math.min(input.scrollHeight, 110) + 'px';
  }

  function submit() {
    const text = input.value.trim();
    if (!text || busy || !ENDPOINT) return;
    input.value = '';
    autosize();
    send(text);
  }

  async function send(text) {
    removeChips();
    appendUser(text);
    messages.push({ role: 'user', content: text });
    setBusy(true);
    const typing = appendTyping();

    try {
      const res = await fetch(ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ messages: messages.slice(-10), session_id: SESSION_ID }),
      });
      typing.remove();

      if (res.status === 429) { appendBot('You’re sending messages a little fast — give me a few seconds and try again. 🙂'); return; }
      if (!res.ok) { appendBot('Sorry, I hit a snag just now. Please try again in a moment.'); return; }

      const data = await res.json();
      const reply = (data && data.reply) ? data.reply : 'Sorry, I didn’t catch that — could you rephrase?';
      appendBot(reply);
      messages.push({ role: 'assistant', content: reply });
      if (data && Array.isArray(data.products) && data.products.length) {
        appendProducts(data.products);
      }
      if (data && data.handoff) {
        appendHandoff(data.handoff);
      }
      if (data && Array.isArray(data.architects) && data.architects.length) {
        appendArchitects(data.architects);
      }
    } catch (err) {
      typing.remove();
      appendBot('I couldn’t reach the store just now — please check your connection and try again.');
    } finally {
      setBusy(false);
      input.focus();
      saveState();   // persist the full exchange (bubbles + cards) for the next page
    }
  }

  function setBusy(state) {
    busy = state;
    sendBtn.disabled = state;
    input.disabled = state;
  }

  // ---- Rendering ---------------------------------------------------------
  function appendUser(text) {
    const el = document.createElement('div');
    el.className = 'nld-chat-msg nld-chat-msg-user';
    el.textContent = text;
    log.appendChild(el);
    scroll();
  }

  function appendBot(text) {
    const el = document.createElement('div');
    el.className = 'nld-chat-msg nld-chat-msg-bot';
    el.innerHTML = formatReply(text);
    log.appendChild(el);
    scroll();
    return el;
  }

  function appendTyping() {
    const el = document.createElement('div');
    el.className = 'nld-chat-typing';
    el.innerHTML = '<span></span><span></span><span></span>';
    log.appendChild(el);
    scroll();
    return el;
  }

  function appendProducts(products) {
    const wrap = document.createElement('div');
    wrap.className = 'nld-chat-products';
    for (const p of products.slice(0, 6)) wrap.appendChild(renderCard(p));
    log.appendChild(wrap);
    scroll();
  }

  function renderCard(p) {
    const a = document.createElement('a');
    a.className = 'nld-chat-card';
    a.href = p.url || ('/products/' + (p.handle || ''));
    a.target = '_top';

    if (p.image_url) {
      const img = document.createElement('img');
      img.className = 'nld-chat-card-img';
      img.src = p.image_url; img.alt = p.title || ''; img.loading = 'lazy';
      a.appendChild(img);
    } else {
      const ph = document.createElement('div');
      ph.className = 'nld-chat-card-img-empty';
      ph.textContent = 'No image';
      a.appendChild(ph);
    }

    const body = document.createElement('div');
    body.className = 'nld-chat-card-body';
    if (p.vendor) body.appendChild(make('div', 'nld-chat-card-vendor', p.vendor));
    body.appendChild(make('div', 'nld-chat-card-title', p.title || 'Product'));
    if (p.price_display) body.appendChild(make('div', 'nld-chat-card-price', p.price_display));
    a.appendChild(body);
    return a;
  }

  function appendArchitects(list) {
    const wrap = document.createElement('div');
    wrap.className = 'nld-chat-products';
    for (const a of list.slice(0, 4)) {
      const card = document.createElement('a');
      card.className = 'nld-chat-card nld-chat-arch';
      card.href = a.url || '#';
      card.target = '_top';
      const body = document.createElement('div');
      body.className = 'nld-chat-card-body';
      if (a.area) body.appendChild(make('div', 'nld-chat-card-vendor', a.area));
      body.appendChild(make('div', 'nld-chat-card-title', a.company || a.name || 'Designer'));
      const link = make('div', 'nld-chat-card-price', 'View profile →');
      body.appendChild(link);
      card.appendChild(body);
      wrap.appendChild(card);
    }
    log.appendChild(wrap);
    scroll();
  }

  function appendHandoff(h) {
    const card = document.createElement('div');
    card.className = 'nld-chat-handoff';

    const title = make('div', 'nld-chat-handoff-title', 'Talk to our team');
    card.appendChild(title);

    const row = document.createElement('div');
    row.className = 'nld-chat-handoff-actions';

    if (h.whatsapp) {
      const intro = 'Hi Next Level Decor — ';
      const msg = encodeURIComponent(intro + (h.summary || 'I have a question.'));
      row.appendChild(actionBtn('whatsapp', 'WhatsApp', `https://wa.me/${h.whatsapp}?text=${msg}`));
    }
    if (h.phone) {
      row.appendChild(actionBtn('call', 'Call', `tel:${h.phone.replace(/[^\d+]/g, '')}`));
    }
    if (h.email) {
      row.appendChild(actionBtn('email', 'Email', `mailto:${h.email}`));
    }
    card.appendChild(row);

    if (h.hours) card.appendChild(make('div', 'nld-chat-handoff-hours', h.hours));
    log.appendChild(card);
    scroll();
  }

  function actionBtn(kind, label, href) {
    const a = document.createElement('a');
    a.className = 'nld-chat-handoff-btn nld-chat-handoff-' + kind;
    a.href = href;
    a.target = '_top';
    a.rel = 'noopener';
    a.textContent = label;
    return a;
  }

  function renderChips(items) {
    const wrap = document.createElement('div');
    wrap.className = 'nld-chat-chips';
    wrap.dataset.nldChatChips = '';
    for (const text of items) {
      const chip = make('button', 'nld-chat-chip', text);
      chip.type = 'button';
      chip.addEventListener('click', () => send(text));
      wrap.appendChild(chip);
    }
    log.appendChild(wrap);
    scroll();
  }
  function removeChips() {
    const chips = log.querySelector('[data-nld-chat-chips]');
    if (chips) chips.remove();
  }

  // ---- Helpers -----------------------------------------------------------
  function make(tag, cls, text) {
    const el = document.createElement(tag);
    el.className = cls;
    if (text != null) el.textContent = text;
    return el;
  }

  function scroll() { log.scrollTop = log.scrollHeight; }

  function readMeta(name) {
    const m = document.querySelector(`meta[name="${name}"]`);
    return m ? m.getAttribute('content') : '';
  }

  /** Minimal, SAFE markdown: escape first, then **bold** and line breaks. */
  function formatReply(text) {
    const esc = String(text)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    const bold = esc.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
    return bold
      .split(/\n{2,}/)
      .map((para) => '<p>' + para.replace(/\n/g, '<br>') + '</p>')
      .join('');
  }

  // Re-open + restore the chat if it was open when the shopper navigated.
  try { if (sessionStorage.getItem(LS_OPEN) === 'true') open(); } catch (e) {}
})();
